import { ExistingSystemType } from 'data/types';
import { compositionHasFuel } from 'utils/composition-picks';
import {
  componentsHaveRequiredAttributes,
  isRecommendationStripCompleted
} from 'utils/existing-equipment';
import {
  isPtacExistingUnitsComplete,
  isPtacPreferencesComplete
} from 'utils/ptac-preferences';
import { PtacPreferencesRecord } from '@alpinehomeair/common';
import { CompositionPicks } from 'utils/system-composition-types';
import {
  calcTonnageFromBtu,
  deriveEquipmentTonnage,
  isUnsureTonnage,
  resolveEffectiveTonnage,
  TonnageRecord
} from 'utils/tonnage-logic';

export type EquipmentPreferencesEntry = {
  wantIds: number[];
  virtualFuel?: string | null;
  tonnage?: TonnageRecord | null;
  ptac?: PtacPreferencesRecord | null;
};

export function wantIdsAreEqual(left: number[], right: number[]): boolean {
  return (
    left.length === right.length &&
    left.every((id, index) => id === right[index])
  );
}

export function isPtacExistingSystem(
  system: Pick<ExistingSystemType, 'type'>
): boolean {
  return system.type === 'ptac';
}

export function isPtacEquipmentPreferencesComplete(
  existingSystems: ExistingSystemType[],
  recommendationSelectionsBySystem: Record<
    string,
    EquipmentPreferencesEntry | null
  >
): boolean {
  const ptacSystems = existingSystems.filter(isPtacExistingSystem);
  return (
    ptacSystems.length > 0 &&
    ptacSystems.every((system) =>
      isPtacPreferencesComplete(
        recommendationSelectionsBySystem[system.uniqueId]?.ptac
      )
    )
  );
}

export function isPtacExistingEquipmentComplete(
  existingSystems: ExistingSystemType[],
  recommendationSelectionsBySystem: Record<
    string,
    EquipmentPreferencesEntry | null
  >
): boolean {
  const ptacSystems = existingSystems.filter(isPtacExistingSystem);
  return (
    ptacSystems.length > 0 &&
    ptacSystems.every((system) =>
      isPtacExistingUnitsComplete(
        recommendationSelectionsBySystem[system.uniqueId]?.ptac?.existing
      )
    )
  );
}

export function equipmentPreferencesSizeAnswered(
  entry: EquipmentPreferencesEntry | null | undefined,
  components: ExistingSystemType['components'] = [],
  coolingBtu: number | null = null
): boolean {
  const record = entry?.tonnage ?? null;
  if (isUnsureTonnage(record)) return true;
  return (
    resolveEffectiveTonnage({
      record,
      equipment: deriveEquipmentTonnage(components),
      calc: calcTonnageFromBtu(coolingBtu)
    }) !== null
  );
}

function equipmentPreferencesHasFuel(
  existingSystems: ExistingSystemType[],
  recommendationSelectionsBySystem: Record<
    string,
    EquipmentPreferencesEntry | null
  >,
  compositionPicksBySystem?: Record<string, CompositionPicks>
): boolean {
  return existingSystems.some((system) => {
    const fuel = recommendationSelectionsBySystem[system.uniqueId]?.virtualFuel;
    if (typeof fuel === 'string' && fuel.length > 0) return true;
    return compositionHasFuel(compositionPicksBySystem?.[system.uniqueId]);
  });
}

export function isEquipmentPreferencesComplete(args: {
  recommendationSelectionsLoaded: boolean;
  existingSystems: ExistingSystemType[];
  recommendationSelectionsBySystem: Record<
    string,
    EquipmentPreferencesEntry | null
  >;
  compositionPicksBySystem?: Record<string, CompositionPicks>;
  coolingBtu?: number | null;
}): boolean {
  if (!args.recommendationSelectionsLoaded) return false;
  const gatedSystems = args.existingSystems.filter(
    (system) => !isPtacExistingSystem(system)
  );
  if (gatedSystems.length === 0) {
    return isPtacEquipmentPreferencesComplete(
      args.existingSystems,
      args.recommendationSelectionsBySystem
    );
  }
  if (
    !equipmentPreferencesHasFuel(
      gatedSystems,
      args.recommendationSelectionsBySystem,
      args.compositionPicksBySystem
    )
  ) {
    return false;
  }
  return gatedSystems.every((system) => {
    const entry = args.recommendationSelectionsBySystem[system.uniqueId];
    const picks = args.compositionPicksBySystem?.[system.uniqueId];
    return (
      componentsHaveRequiredAttributes(system.components) &&
      isRecommendationStripCompleted(entry, picks) &&
      equipmentPreferencesSizeAnswered(
        entry,
        system.components,
        args.coolingBtu
      )
    );
  });
}
